import {
  QueryFunctionContext,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from 'react-query';

import { Meta } from '@custom-types/Response';

import {
  list as listFn,
  get as getFn,
  ListParams,
  Lead,
  create as createFn,
  update as updateFn,
  Payload,
  getCustomFieldsConfig as getCustomFieldsConfigFn,
} from './service';

interface LeadsArguments {
  eventId: string;
  companyId: string;
  params?: ListParams;
}

const LeadListKeys: ['leads', 'list'] = ['leads', 'list'];
type LeadsKey = ['leads', 'list', string /* eventId */, string /* companyId */, ListParams?];

const leadsQuery = {
  getQueryKey: (eventId: string, companyId: string, params?: ListParams): LeadsKey => [
    LeadListKeys[0],
    LeadListKeys[1],
    eventId,
    companyId,
    params,
  ],
  queryFn: ({
    pageParam,
    queryKey: [, , eventId, companyId, params],
  }: QueryFunctionContext<LeadsKey>) =>
    listFn(eventId, companyId, { page: pageParam || 0, ...params }),
};

export const useLeads = ({ eventId, companyId, params }: LeadsArguments) =>
  useInfiniteQuery({
    queryKey: leadsQuery.getQueryKey(eventId, companyId, params),
    queryFn: leadsQuery.queryFn,
  });

export const useLeadCount = ({ eventId, companyId, params }: LeadsArguments) =>
  useQuery({
    queryKey: leadsQuery.getQueryKey(eventId, companyId, params),
    queryFn: leadsQuery.queryFn,
    select: (res: { data: Lead[]; meta: Meta }) => res?.meta?.pagination?.totalItemsCount,
  });

const LeadDetailsKey: ['lead', 'details'] = ['lead', 'details'];

type LeadKey = [
  'lead',
  'details',
  string /* eventId */,
  string /* companyId */,
  string? /* leadId */,
  string? /* leadEmail */
];

const leadQuery = {
  getQueryKey: (
    eventId: string,
    companyId: string,
    leadId?: string,
    leadEmail?: string
  ): LeadKey => [LeadDetailsKey[0], LeadDetailsKey[1], eventId, companyId, leadId, leadEmail],
  queryFn: ({ queryKey: [, , eventId, companyId, leadId] }: QueryFunctionContext<LeadKey>) => {
    if (!leadId) {
      throw new Error('Lead Id not found');
    }

    return getFn(eventId, companyId, leadId);
  },
  queryFnEmail: ({
    queryKey: [, , eventId, companyId, , leadEmail],
  }: QueryFunctionContext<LeadKey>) => {
    if (!leadEmail) {
      throw new Error('Lead email not found');
    }

    return listFn(eventId, companyId, { limit: 1, email: leadEmail });
  },
};

export const useLead = (
  eventId: string,
  companyId: string,
  leadId?: string,
  leadEmail?: string
) => {
  const detailsById = useQuery({
    queryKey: leadQuery.getQueryKey(eventId, companyId, leadId, leadEmail),
    queryFn: leadQuery.queryFn,
    enabled: !!leadId,
  });

  const detailsByEmail = useQuery({
    queryKey: leadQuery.getQueryKey(eventId, companyId, leadId, leadEmail),
    queryFn: leadQuery.queryFnEmail,
    enabled: Boolean(leadEmail),
  });

  if (leadId) {
    return detailsById;
  }

  return {
    ...detailsByEmail,
    data: detailsByEmail.data?.data[0],
    isLoading: detailsByEmail.isFetching && detailsByEmail.isLoading,
  };
};

export const useCreateLead = (eventId: string, companyId: string) => {
  const queryClient = useQueryClient();

  return useMutation((payload: Payload) => createFn(eventId, companyId, payload), {
    onSuccess: () =>
      queryClient.invalidateQueries(LeadListKeys, {
        refetchType: 'all',
      }),
  });
};

export const useUpdateLead = (eventId: string, companyId: string) => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ leadId, payload }: { leadId: string; payload: Payload }) =>
      updateFn(eventId, companyId, leadId, payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(LeadListKeys, {
          refetchType: 'all',
        });
        /*
          TODO: need to rethink this implementation when we are supporting the offline feature
          Right now this is resetting all the cached detail APIs. We will not want this when moving to
          offline support.
          Ideally should be using queryClient.resetQueries(leadQuery.getQueryKey(eventId, companyId, leadId));
          but for some reason the old data is displayed on Edit form. 
          Need investigation on how the Tabs are working
        */
        queryClient.resetQueries(LeadDetailsKey);
      },
    }
  );
};

const CustomFieldConfig: ['companies', 'customField'] = ['companies', 'customField'];
type CustomFieldConfigKey = [
  'companies',
  'customField',
  string /* eventId */,
  string /* companyId */
];

const customFieldQuery = {
  getQueryKey: (eventId: string, companyId: string): CustomFieldConfigKey => [
    CustomFieldConfig[0],
    CustomFieldConfig[1],
    eventId,
    companyId,
  ],
  queryFn: ({ queryKey: [, , eventId, companyId] }: QueryFunctionContext<CustomFieldConfigKey>) =>
    getCustomFieldsConfigFn(eventId, companyId),
};

export const useGetCustomFieldsConfig = (eventId: string, companyId: string) => {
  const details = useQuery({
    queryKey: customFieldQuery.getQueryKey(eventId, companyId),
    queryFn: customFieldQuery.queryFn,
  });

  return {
    data: details.data,
    isLoading: details.isFetching && details.isLoading,
  };
};
